import { reservationSchema, memberReportSchema, hotelSchema } from 'store/schemas/database.schemas';
import { normalize } from 'normalizr';
import { putDatabase } from 'store/actions/database.actions';
import ReservationsRepository from 'services/repositories/ReservationsRepository';
import { actionCreators, actionCreators as reservationsActionCreators } from 'store/actions/metaDatabase.reservations.actions';
import { buildMetaReservationSelector, buildReservationSelector } from 'store/selectors/reservations.selector';
import ReservationBundlesRepository from 'services/repositories/ReservationBundlesRepository';
import { buildMetaReportsSelector } from 'store/selectors/memberReports.selector';
import ReservationBundleComponentsRepository from 'services/repositories/ReservationBundleComponentsRepository';
import InsurancesRepository from 'services/repositories/InsurancesRepository';
import { actionCreators as memberReportsActionCreators } from 'store/actions/metaDatabase.memberReports.actions';
import {
    saveEntity, buildRepositoryAction, loadEntityWithQueue, loadAll,
} from './helpers';

export const pendingPaymentIncludes = [
    'reservationHotels.hotel.destination.parent',
    'request.branding',
    'numbers',
    'reservationDates',
    'request.user.userEmails',
    'request.member',
];

export const reservationTableIncludes = [
    'reservationHotels.hotel.destination.parent',
    'reservationServices.vendorService',
    'reservationFlights',
    'reservationInsurances',
    'reservationInsurances.currency',
    'reservationHotels.currency',
    'reservationServices.currency',
    'reservationFlights.currency',
    'reservationDates',
    'request.user.userEmails',
    'request.branding',
    'request.member',
];

export const reservationComponentIncludes = [
    'bundledInvoicePayments',
    'members',
    'bundles.components',
    'numbers',
    'request.branding',
    'request.member',
    'request.member.travelAgency',
    'request.user.userEmails',
    'request.user.conciergeGroups.concierges.emails',
    'reservationAssistants',
    'reservationDates',
    'reservationDocuments',
    'reservationFlights.currency',
    'reservationFlights.numbers',
    'reservationFlights.reservationFlightLegs.airline',
    'reservationFlights.reservationFlightLegs.departureFromAirport',
    'reservationFlights.reservationFlightLegs.departureToAirport',
    'reservationFlights.reservationFlightMembers',
    'reservationFlights.reservationFlightMemberRelations',
    'reservationInsurances.currency',
    'reservationInsurances.insurance',
    'reservationInsurances.numbers',
    'reservationHotels.currency',
    'reservationHotels.hotel.destination.parent',
    'reservationHotels.hotel.destination.destinationDocuments',
    'reservationHotels.hotel.defaultServices',
    'reservationHotels.hotel.defaultServices.vendor',
    'reservationHotels.hotel.hotelContacts.emails',
    'reservationHotels.reservationHotelDates',
    'reservationHotels.members',
    'reservationHotels.numbers',
    'reservationHotels.reservationHotelRooms.reservationHotelRoomMembers',
    'reservationHotels.reservationHotelRooms.hotelRoom',
    'reservationServices.currency',
    'reservationServices.numbers',
    'reservationServices.reservationServiceMembers',
    'reservationServices.vendorService.vendor',
    'reservationTravelingMembers',
];

export function loadReservation(id, includes = null)
{
    return async (dispatch) =>
    {
        const result = await dispatch(loadEntityWithQueue(id, includes ?? reservationComponentIncludes, buildMetaReservationSelector, actionCreators, new ReservationsRepository(), reservationSchema));
        if (result?.isSuccess())
        {
            const reservation = result?.response;
            const hotels = reservation?.reservationHotels?.map((reservationHotel) => reservationHotel.hotel);
            const normalized = normalize(hotels.flat(), [hotelSchema]);
            dispatch(putDatabase(normalized.entities));
        }
        if (result?.httpClientResponse?.status === 401)
        {
            window.location.replace('/app/401');
        }
    };
}

export function saveReservation(data)
{
    return saveEntity(data.id, actionCreators, buildRepositoryAction(new ReservationsRepository(), 'save', [data]), reservationSchema);
}

export function loadPendingPayments()
{
    return async (dispatch, getState) =>
    {
        const reportId = 'pending_payments';
        const metaReportsSelector = buildMetaReportsSelector();
        const metaEntity = metaReportsSelector(getState(), reportId);

        if (metaEntity.loading) return;

        dispatch(memberReportsActionCreators._loading(reportId, true, pendingPaymentIncludes));

        const repository = new ReservationsRepository();
        const result = await repository.getPendingPayments(pendingPaymentIncludes);
        dispatch(memberReportsActionCreators._loading(reportId, false, pendingPaymentIncludes));

        if (!result.isSuccess())
        {
            dispatch(memberReportsActionCreators._error(reportId, result.mergeMessages()));
            return;
        }

        const report = { id: reportId, reservations: result.response };
        const schemaResult = normalize(report, memberReportSchema);

        dispatch(putDatabase(schemaResult.entities));

        Object.values(result.response).forEach((reservation) =>
        {
            dispatch(actionCreators._setLoadedIncludes(reservation.id, pendingPaymentIncludes));
        });
    };
}

export function loadRecentReservations(query)
{
    const summaryIncludes = reservationTableIncludes;
    return async (dispatch, getState) =>
    {
        const reportId = 'recent_reservations';
        const metaReportsSelector = buildMetaReportsSelector();
        const metaEntity = metaReportsSelector(getState(), reportId);

        if (metaEntity.loading) return;
        dispatch(memberReportsActionCreators._loading(reportId, true, []));

        const reservationRepository = new ReservationsRepository();
        const result = await reservationRepository.all({ ...query, includes: summaryIncludes });
        dispatch(memberReportsActionCreators._loading(reportId, false, []));

        if (!result.isSuccess())
        {
            dispatch(memberReportsActionCreators._error(reportId, result.mergeMessages()));
            return;
        }

        const report = { id: reportId, reservations: result.response.data };
        const schemaResult = normalize(report, memberReportSchema);
        dispatch(putDatabase(schemaResult.entities));

        Object.values(result.response.data).forEach((reservation) =>
        {
            dispatch(actionCreators._setLoadedIncludes(reservation.id, summaryIncludes));
        });
    };
}

export function loadReservations(query, includes = null)
{
    const summaryIncludes = includes ?? reservationTableIncludes;

    return async (dispatch) =>
    {
        const response = await dispatch(
            loadAll(query, summaryIncludes, buildMetaReservationSelector, actionCreators, new ReservationsRepository(), reservationSchema, query),
        );

        if (!response)
        {
            return null;
        }
        Object.values(response.response.data).forEach((reservation) =>
        {
            dispatch(actionCreators._setLoadedIncludes(reservation.id, summaryIncludes));
        });

        return response.response;
    };
}

export function saveReservationComponentMembers(reservationId, data)
{
    return async () =>
    {
        const reservationRepository = new ReservationsRepository();
        const result = await reservationRepository.setComponentMembers(reservationId, data);

        return result.response ?? [];
    };
}

export function getAvailableInsurances(reservationId)
{
    return async () =>
    {
        const repository = new InsurancesRepository();
        const response = await repository.getAvailableInsurances(reservationId);

        return response.response;
    };
}

export async function requestInsurance(reservationId, insuranceId, travelers, authMember, insuranceName, insuranceAmount, agentEmail)
{
    const repository = new InsurancesRepository();
    const request = await repository.requestInsurance({
        insurance_id: insuranceId,
        reservation_id: reservationId,
        members: travelers,
        insuranceName,
        insuranceAmount,
        authMember,
        agentEmail,
    });

    const { response } = request;

    if (request.response_code !== 200)
    {
        if (response?.errors)
        {
            return {
                errors: response.errors,
            };
        }
        if (response.message)
        {
            throw new Error(response.message);
        }
        throw new Error('Error sending information, try again!');
    }

    return response[0];
}
export function storeInformativeServiceToReservation(reservationId, data)
{
    return async (dispatch, getState) =>
    {
        const metaReservationSelector = buildMetaReservationSelector();
        const metaEntity = metaReservationSelector(getState(), reservationId);

        if (metaEntity.loading) return;

        const reservationsRepository = new ReservationsRepository();

        const result = await reservationsRepository.addInformativeService(reservationId, data);
        if (!result.isSuccess())
        {
            const { response } = result;
            if (response.errors)
            {
                throw new Error(Object.values(response.errors).join('<br />'));
            }
            throw new Error(response.error.message ?? 'Error saving the Informative Service, try again');
        }

        const reservationSelector = buildReservationSelector();
        const reservation = reservationSelector(getState(), reservationId);
        dispatch(reservationsActionCreators._needUpdate(reservation.id, true));
    };
}

export function storeInformativeFlightToReservation(reservationId, data)
{
    return async (dispatch, getState) =>
    {
        const metaReservationSelector = buildMetaReservationSelector();
        const metaEntity = metaReservationSelector(getState(), reservationId);

        if (metaEntity.loading) return;

        const reservationsRepository = new ReservationsRepository();

        const result = await reservationsRepository.addInformativeFlight(reservationId, data);
        if (!result.isSuccess())
        {
            const { response } = result;
            if (response.errors)
            {
                throw new Error(Object.values(response.errors).join('<br />'));
            }
            throw new Error(response.error.message ?? 'Error saving the Informative Flight, try again');
        }

        const reservationSelector = buildReservationSelector();
        const reservation = reservationSelector(getState(), reservationId);
        dispatch(reservationsActionCreators._needUpdate(reservation.id, true));
    };
}
export function storeInformativeHotelToReservation(reservationId, data)
{
    return async (dispatch, getState) =>
    {
        const metaRequestQuoteSelector = buildMetaReservationSelector();
        const metaEntity = metaRequestQuoteSelector(getState(), reservationId);

        if (metaEntity.loading) return;

        const requestQuotesRepository = new ReservationsRepository();

        dispatch(actionCreators._loading(reservationId, true, []));
        const result = await requestQuotesRepository.addInformativeHotel(reservationId, data);
        dispatch(actionCreators._loading(reservationId, false, []));

        if (!result.isSuccess())
        {
            dispatch(actionCreators._error(reservationId, result.mergeMessages()));
            return;
        }

        const reservationSelector = buildReservationSelector();
        const reservation = reservationSelector(getState(), reservationId);
        dispatch(actionCreators._needUpdate(reservation.id, true));
    };
}

export function patchInformativeHotelToReservation(reservationId, requestServiceId, data)
{
    return async (dispatch, getState) =>
    {
        const metaRequestQuoteSelector = buildMetaReservationSelector();
        const metaEntity = metaRequestQuoteSelector(getState(), reservationId);

        if (metaEntity.loading) return;

        const reservationsRepository = new ReservationsRepository();

        dispatch(actionCreators._loading(reservationId, true, []));
        const result = await reservationsRepository.updateInformativeHotel(reservationId, requestServiceId, data);
        dispatch(actionCreators._loading(reservationId, false, []));

        if (!result.isSuccess())
        {
            dispatch(actionCreators._error(reservationId, result.mergeMessages()));
            return;
        }

        const reservationSelector = buildReservationSelector();
        const reservation = reservationSelector(getState(), reservationId);
        dispatch(actionCreators._needUpdate(reservation.id, true));
    };
}

export function storeReservationBundle(data)
{
    return async (dispatch) =>
    {
        const { reservationId } = data;

        const reservationBundlesRepository = new ReservationBundlesRepository();

        dispatch(actionCreators._loading(reservationId, true, []));
        const result = await reservationBundlesRepository.storeReservationBundle(data);
        dispatch(actionCreators._loading(reservationId, false, []));
        if (!result.isSuccess())
        {
            dispatch(actionCreators._error(reservationId, result.mergeMessages()));
        }

        return result.response;
    };
}

export function deleteReservationBundle(reservationId, reservationBundleId)
{
    return async (dispatch, getState) =>
    {
        const metaReservationSelector = buildMetaReservationSelector();
        const metaEntity = metaReservationSelector(getState(), reservationId);

        if (metaEntity.loading) return;

        const reservationBundlesRepository = new ReservationBundlesRepository();

        dispatch(actionCreators._loading(reservationId, true, []));
        const result = await reservationBundlesRepository.deleteReservationBundle(reservationBundleId);
        dispatch(actionCreators._loading(reservationId, false, []));
        if (!result.isSuccess())
        {
            dispatch(actionCreators._error(reservationId, result.mergeMessages()));
        }

        const reservationSelector = buildReservationSelector();
        const reservation = reservationSelector(getState(), reservationId);
        const reservationBundles = reservation?.bundles?.map((reservationBundle) =>
        {
            if (reservationBundle.id === reservationBundleId)
            {
                return null;
            }
            return reservationBundle;
        }).filter((n) => n);

        if (reservationBundles)
        {
            const n = normalize({ ...reservation, bundles: reservationBundles }, reservationSchema);
            dispatch(putDatabase(n.entities));
        }
    };
}

export function addReservationBundle(reservationId, reservationBundle)
{
    return async (dispatch, getState) =>
    {
        const reservationSelector = buildReservationSelector();
        const entity = reservationSelector(getState(), reservationId);
        const reservationBundles = Object.assign([], entity.bundles);
        reservationBundles.push(reservationBundle);

        const n = normalize({ ...entity, bundles: reservationBundles }, reservationSchema);
        dispatch(putDatabase(n.entities));
    };
}

export function storeReservationBundleComponent(data)
{
    return async (dispatch, getState) =>
    {
        const { reservationId, reservationBundleId } = data;
        const reservationBundleComponentsRepository = new ReservationBundleComponentsRepository();

        dispatch(actionCreators._loading(reservationId, true, []));
        const result = await reservationBundleComponentsRepository.storeReservationBundleComponent(data);
        dispatch(actionCreators._loading(reservationId, false, []));

        if (!result.isSuccess())
        {
            dispatch(actionCreators._error(reservationId, result.mergeMessages()));
            return;
        }

        const reservationSelector = buildReservationSelector();
        const reservation = reservationSelector(getState(), reservationId);

        const reservationBundles = reservation?.bundles?.map((reservationBundle) =>
        {
            if (reservationBundle.id !== reservationBundleId)
            {
                return reservationBundle;
            }
            return { ...reservationBundle, components: result.response };
        });

        if (reservationBundles)
        {
            const n = normalize({ ...reservation, bundles: reservationBundles }, reservationSchema);
            dispatch(putDatabase(n.entities));
        }
    };
}
